import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

export type ErasureStatus = 'REQUESTED' | 'APPROVED' | 'EXECUTED' | 'CANCELLED'

export interface ErasureRow {
  requestId: string
  customerId: string
  status: ErasureStatus
  requestedByUserId: string | null
  requestedReason: string | null
  approvedByUserId: string | null
  approvedAt: string | null
  executedAt: string | null
  cancelledReason: string | null
  cancelledAt: string | null
  createdAt: string
}

export interface FileRequestInput {
  customerId: string
  reason: string
}

export interface ExecuteResponse {
  requestId: string
  customerId: string
  rowsTouched: number
}

export function useErasureRequests(status: ErasureStatus, limit = 100) {
  return useQuery({
    queryKey: ['erasure', 'list', status, limit],
    queryFn: async () => {
      const res = await api.get<ErasureRow[]>('/v1/erasure-requests', {
        params: { status, limit },
      })
      return res.data
    },
    refetchInterval: 30_000,
  })
}

export function useFileErasureRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ customerId, reason }: FileRequestInput) => {
      const res = await api.post<{ requestId: string }>(
        `/v1/customers/${customerId}/erasure-requests`,
        { reason },
      )
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['erasure'] }),
  })
}

export function useExecuteErasure() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (requestId: string) => {
      const res = await api.post<ExecuteResponse>(`/v1/erasure-requests/${requestId}/execute`)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['erasure'] }),
  })
}

export function useCancelErasure() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: string; reason: string }) => {
      await api.delete(`/v1/erasure-requests/${requestId}`, { params: { reason } })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['erasure'] }),
  })
}
